import React, { useContext, useState } from 'react'
import { Button, ListMenu, ListMenuButton, Modal, NotificationInline,
   NotificationManagerContext, Popover, Spacer, Text, View } from 'oio-react'
import { Link, Redirect, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom'
import { DocumentIcon, MoreIcon } from 'assets/icons'
import uiConstants from 'config/constants/ui'
import PageDeleteConfirm from 'src/apps/Page/components/DeleteConfirm'
import PageCreate from 'src/apps/Page/pages/Create'
import ScheduleEditor from 'src/components/Schedule/Editor'
import ProtoContentListItemRow from 'src/components/Settings/ProtoContentListItemRow'
import ProtoHeadingBlock from 'src/components/Settings/ProtoHeadingBlock'
import ProtoReturnBar from 'src/components/Settings/ProtoReturnBar'
import { initiativeSettingsUrl } from 'src/components/utils/urls'
import { InitiativeHierarchyContext } from 'src/libraries/contexts/InitiativeHierarchy'
import ThemeContext from 'src/libraries/contexts/Theme'
import { usePageList, useUpdateInitiative, useUpdatePage } from 'src/graphql/hooks'
import PageVisualEditor from './VisualEditor'

const InitiativeContentSettings = () => {
   const history = useHistory()
   const match = useRouteMatch()
   const [modalIsOpen, setModalIsOpen] = useState(true)
   const { showNotification } = useContext(NotificationManagerContext)
   const theme = useContext(ThemeContext)

   const [pageDeleteModalIsOpen, setPageDeleteModalIsOpen] = useState(false)
   const [popoverConfig, setPopoverConfig] = useState({
      anchorElement: null,
      entityType: null,
      focusedValue: null,
      isOpen: false
   })

   const { initiative } = useContext(InitiativeHierarchyContext)
   const { updateInitiative, mutating: initiativeUpdating } = useUpdateInitiative()
   const { updatePage, mutating: pageUpdating } = useUpdatePage()

   const pageListFilter = {
      archived: 'include',
      unpublished: 'include',
      initiativeId: initiative.id,
      limit: 25
   }

   const { pageList, refetch: refetchPageList } = usePageList(pageListFilter)
   const publishedPages = pageList.items.filter(p => p.published)
   const unpublishedPages = pageList.items.filter(p => !p.published)

   // TODO: will have to add this back - likely under Initiative Home Editing
   // Keep this is so Steven does not forget in next PR
   // Probably do when we do messages
   // {
   //    id: 'appsMessagesEnabled',
   //    name: 'Comments',
   //    enabled: initiative.appsMessagesEnabled
   // },

   const apps = [{
      enabledFieldName: 'appsFilesEnabled',
      name: 'Files',
      enabled: initiative.appsFilesEnabled
   }, {
      enabledFieldName: 'appsPeopleEnabled',
      name: 'People',
      enabled: initiative.appsPeopleEnabled
   }]

   if (initiative.class === 'event') {
      apps.push({
         enabledFieldName: 'appsScheduleEnabled',
         name: 'Schedule',
         editUrl: `${match.url}/schedule-editor`,
         enabled: initiative.appsScheduleEnabled
      })
   }

   const enabledApps = apps.filter(a => a.enabled)
   const disabledApps = apps.filter(a => !a.enabled)

   const handleToggleAppEnabled = async (field) => {
      const newEnabled = !initiative[field]
      await updateInitiative({ id: initiative.id }, { [field]: newEnabled })
      showNotification({
         message: 'Changes saved successfully!',
         title: 'Success!',
         type: 'success'
      })
   }

   const handleTogglePagePublished = async (pageId, published) => {
      await updatePage({ id: pageId, withContent: false }, { published })
      showNotification({
         message: 'Changes saved successfully!',
         title: 'Success!',
         type: 'success'
      })
   }

   const handleClosePopover = () => {
      setPopoverConfig(config => ({
         ...config,
         isOpen: false,
         anchorElement: null
      }))
   }

   return (
      <View>
         <ProtoReturnBar
            display="block[a-c] none[d-f]"
            returnLinkName={`${initiative.type.nameSingular} Settings`}
            returnLinkTo={initiativeSettingsUrl()}
         />
         <View
            display="flex"
            justifyContent="center"
            width="100%">
            <View
               maxWidth="100%[a-c] 660px[d-f]"
               padding="20px[a] 25px[b] 30px[c] 40px[d-f]">
               <View width="100%">
                  <View
                     float="left"
                     width="100%"
                     display="flex">
                     <View paddingRight="30px[b-f]">
                        <ProtoHeadingBlock
                           title={`${initiative.type.nameSingular} Tabs & Content`}
                           description={`Create and organize all the content for this ${initiative.type.nameSingular}. Only tabs that are published will be visible to non-admin users.`}
                           icon={(
                              <DocumentIcon
                                 width="14px"
                                 height="14px"
                                 color="#fff"
                                 strokeWidth="2px"
                              />
                           )}
                        />
                     </View>
                     <View flex="0 0 auto">
                        <Button
                           linkTo={`${match.url}/create`}
                           name="Add Custom Tab"
                           size="md"
                           color={theme.protoSettingsHighlightBackgroundColor}
                           textColor={theme.protoSettingsHighlightForegroundColor}
                           textSize="0.9"
                           textWeight="medium"
                        />
                     </View>
                  </View>
                  <View float="left" width="100%" marginBottom="18px">
                     {(initiativeUpdating || pageUpdating) && (
                        <NotificationInline
                           borderRadius="6px"
                           type="loading"
                           title="Updating..."
                           message="Update in progress"
                           paddingHorizontal="12px"
                           paddingVertical="12px"
                           textSize="1.5"
                        />
                     )}
                  </View>
                  <View
                     display="flex"
                     justifyContent="space-between"
                     width="100%"
                     padding="12px 0px">
                     <View>
                        <Text size="2" weight="medium" color="#000">
                           {`Published ${initiative.type.nameSingular} Tabs`}
                        </Text>
                        <Spacer size="0.5" />
                        <Text size="1.5" color="#888">
                           The following tabs are active and visible
                        </Text>
                     </View>
                  </View>
                  <View
                     width="100%"
                     borderRadius="3px"
                     backgroundColor="#fff">
                     <ProtoContentListItemRow
                        key="home-tab"
                        name={`${initiative.type.nameSingular} Home`}
                        description={`Primary Tab with ${initiative.type.nameSingular} overview`}
                        // TODO: This will be added back when pages are implemented properly
                        // rightDetail={(
                        //    <Text size="1.5" color="#888">
                        //       Edit
                        //    </Text>
                        // )}
                     />
                     {publishedPages.map(page => (
                        <ProtoContentListItemRow
                           key={page.id}
                           description="Custom Tab"
                           name={page.title}
                           rightDetail={(
                              <View display="flex" alignItems="center">
                                 <Link to={`${match.url}/${page.slug}/edit`}>
                                    <Text size="1.5" color="#888">
                                       <u>Edit</u>
                                    </Text>
                                 </Link>
                                 <Spacer size="3" orientation="vertical" />
                                 <View
                                    display="flex"
                                    alignItems="center"
                                    position="relative"
                                    style={{ cursor: 'pointer' }}
                                    onClick={(e) => {
                                       setPopoverConfig({
                                          entityType: 'page',
                                          isOpen: true,
                                          anchorElement: e.currentTarget,
                                          focusedValue: page
                                       })
                                    }}>
                                    <MoreIcon
                                       width="24px"
                                       height="24px"
                                       color="#aaa"
                                    />
                                 </View>
                              </View>
                           )}
                        />
                     ))}
                     {enabledApps.map(app => (
                        <ProtoContentListItemRow
                           key={app.name}
                           name={app.name}
                           description="Template Tab"
                           rightDetail={(
                              <View display="flex" alignItems="center">
                                 {app.editUrl && (
                                    <Link to={app.editUrl}>
                                       <Text size="1.5" color="#888">
                                          <u>Edit</u>
                                       </Text>
                                    </Link>
                                 )}
                                 <Spacer size="3" orientation="vertical" />
                                 <View
                                    display="flex"
                                    alignItems="center"
                                    position="relative"
                                    style={{ cursor: 'pointer' }}
                                    onClick={(e) => {
                                       setPopoverConfig({
                                          entityType: 'app',
                                          isOpen: true,
                                          anchorElement: e.currentTarget,
                                          focusedValue: app
                                       })
                                    }}>
                                    <MoreIcon
                                       width="24px"
                                       height="24px"
                                       color="#aaa"
                                    />
                                 </View>
                              </View>
                           )}
                        />
                     ))}
                  </View>
                  {(disabledApps.length > 0 || unpublishedPages.length > 0) && (
                     <>
                        <View width="100%" padding="12px 0px">
                           <Spacer size="3" />
                           <Text size="2" weight="medium" color="#000">
                              {`Unpublished ${initiative.type.nameSingular} Tabs`}
                           </Text>
                           <Spacer size="0.5" />
                           <Text size="1.5" color="#888">
                              The following tabs have not yet been published and are not visible
                           </Text>
                        </View>
                        <View
                           width="100%"
                           borderRadius="3px"
                           backgroundColor="#fff">
                           {disabledApps.map(app => (
                              <ProtoContentListItemRow
                                 key={app.name}
                                 name={app.name}
                                 description="Template Tab"
                                 rightDetail={(
                                    <View display="flex" alignItems="center">
                                       {app.editUrl && (
                                          <Link to={app.editUrl}>
                                             <Text size="1.5" color="#888">
                                                <u>Edit</u>
                                             </Text>
                                          </Link>
                                       )}
                                       <Spacer size="3" orientation="vertical" />
                                       <View
                                          display="flex"
                                          alignItems="center"
                                          position="relative"
                                          style={{ cursor: 'pointer' }}
                                          onClick={(e) => {
                                             setPopoverConfig({
                                                entityType: 'app',
                                                isOpen: true,
                                                anchorElement: e.currentTarget,
                                                focusedValue: app
                                             })
                                          }}>
                                          <MoreIcon
                                             width="24px"
                                             height="24px"
                                             color="#aaa"
                                          />
                                       </View>
                                    </View>
                                 )}
                              />
                           ))}
                           {unpublishedPages.map(page => (
                              <ProtoContentListItemRow
                                 key={page.id}
                                 description="Custom Tab"
                                 name={page.title}
                                 rightDetail={(
                                    <View display="flex" alignItems="center">
                                       <Link to={`${match.url}/${page.slug}/edit`}>
                                          <Text size="1.5" color="#888">
                                             <u>Edit</u>
                                          </Text>
                                       </Link>
                                       <Spacer size="3" orientation="vertical" />
                                       <View
                                          display="flex"
                                          alignItems="center"
                                          position="relative"
                                          style={{ cursor: 'pointer' }}
                                          onClick={(e) => {
                                             setPopoverConfig({
                                                entityType: 'page',
                                                isOpen: true,
                                                anchorElement: e.currentTarget,
                                                focusedValue: page
                                             })
                                          }}>
                                          <MoreIcon
                                             width="24px"
                                             height="24px"
                                             color="#aaa"
                                          />
                                       </View>
                                    </View>
                                 )}
                              />
                           ))}
                        </View>
                     </>
                  )}
               </View>
            </View>
         </View>
         {/* App List Item Popover */}
         <Popover
            anchorElement={popoverConfig.anchorElement}
            anchorOriginHorizontal="right"
            anchorOriginVertical="top"
            backgroundColor="#fff"
            borderRadius="6px"
            width="120px"
            onBodyClick={handleClosePopover}
            open={popoverConfig.isOpen}>
            <View
               float="left"
               width="100%"
               backgroundColor="#fff"
               borderRadius="6px"
               boxShadow="3px 3px 12px rgba(0,0,0,0.2)">
               {popoverConfig.entityType === 'app' && (
                  <ListMenu buttonSize="sm">
                     <ListMenuButton
                        name={popoverConfig.focusedValue?.enabled ? 'Unpublish' : 'Publish'}
                        onClick={() => {
                           handleToggleAppEnabled(popoverConfig.focusedValue?.enabledFieldName)
                        }}
                     />
                  </ListMenu>
               )}
               {popoverConfig.entityType === 'page' && (
                  <ListMenu buttonSize="sm">
                     <ListMenuButton
                        name={popoverConfig.focusedValue?.published ? 'Unpublish' : 'Publish'}
                        onClick={() => {
                           handleTogglePagePublished(
                              popoverConfig.focusedValue?.id,
                              !popoverConfig.focusedValue?.published
                           )
                        }}
                     />
                     <ListMenuButton
                        onClick={() => setPageDeleteModalIsOpen(true)}
                        name="Delete Tab"
                     />
                  </ListMenu>
               )}
            </View>
         </Popover>
         {popoverConfig.entityType === 'page' && (
            <Modal
               borderRadius="6px"
               width="90%[a-b] 300px[c-f]"
               onCloseTrigger={() => setPageDeleteModalIsOpen(false)}
               open={pageDeleteModalIsOpen}>
               <PageDeleteConfirm
                  pageId={popoverConfig.focusedValue?.id}
                  pageTitle={popoverConfig.focusedValue?.title}
                  onCancelButtonClick={() => setPageDeleteModalIsOpen(false)}
                  onDelete={() => {
                     setPageDeleteModalIsOpen(false)
                     refetchPageList()
                  }}
               />
            </Modal>
         )}
         <Switch>
            <Route
               path={`${match.url}/create`}
               render={() => (
                  <Modal
                     borderRadius="6px"
                     width="300px"
                     onCloseTrigger={() => setModalIsOpen(false)}
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     open={modalIsOpen}
                     zIndex={uiConstants.zIndexes.tmpSettingsModal}>
                     <PageCreate
                        onCancelButtonClick={() => setModalIsOpen(false)}
                        onCreate={(page) => {
                           setModalIsOpen(false)
                           refetchPageList()
                        }}
                        targetId={initiative.id}
                        targetType="initiative"
                     />
                  </Modal>
               )}
            />
            <Route
               path={`${match.url}/:pageSlug/edit`}
               render={props => (
                  <Modal
                     width="100%"
                     height="100%"
                     onCloseTrigger={() => setModalIsOpen(false)}
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     open={modalIsOpen}
                     zIndex={uiConstants.zIndexes.tmpSettingsModal}>
                     <PageVisualEditor returnUrl={match.url} />
                  </Modal>
               )}
            />
            <Redirect
               exact
               from={`${match.url}/schedule-editor`}
               to={`${match.url}/schedule-editor/all`}
            />
            <Route
               path={`${match.path}/schedule-editor/:selectedDateKey?`}
               render={() => (
                  <Modal
                     borderRadius="9px"
                     width="100%[a-b] 90%[c] 900px[d-f]"
                     height="100%[a-b] 70%[c-f]"
                     onCloseTrigger={() => setModalIsOpen(false)}
                     onCloseComplete={() => {
                        history.push(match.url)
                        setModalIsOpen(true)
                     }}
                     open={modalIsOpen}
                     zIndex={uiConstants.zIndexes.tmpSettingsModal}>
                     <ScheduleEditor onCloseButtonClick={() => setModalIsOpen(false)} />
                  </Modal>
               )}
            />
         </Switch>
      </View>
   )
}

export default InitiativeContentSettings
